import React from 'react';
import SEO from '../../components/SEO';
import { motion } from 'framer-motion';
import PageBanner from '../../components/PageBanner';

const CapitalBeautyPage = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.8 }
    };

    return (
        <div className="bg-white min-h-screen pt-20 lg:pt-[120px]">
            <SEO
                title="Capital Beauty Design Philosophy | BAIC Design"
                description="Discover 'Capital Beauty' - BAIC's unique design philosophy merging tradition with modernity, confidence, and warmth."
                canonicalUrl="https://baicoman.com/about/capital-beauty"
            />
            <h1 className="sr-only">BAIC Design Philosophy - Capital Beauty</h1>
            {/* Hero Section */}
            {/* Hero Section */}
            <PageBanner
                image="/assets/images/capital-beauty/capital-beauty-redifined.jpeg"
                imageAlt="Capital Beauty Hero"
                contentClassName="items-center text-center"
            >
                <h2 className="text-3xl md:text-8xl lg:text-9xl font-black italic text-slate-700 uppercase tracking-tighter mb-4" style={{ fontFamily: '"Rock Salt", "Brush Script MT", cursive', textShadow: '2px 2px 4px rgba(0,0,0,0.1)' }}>
                    CAPITAL BEAUTY
                </h2>
                <div className="bg-red-700 text-white px-4 py-1 md:py-2 transform -skew-x-12 inline-block">
                    <h2 className="text-xl md:text-6xl lg:text-7xl font-bold uppercase tracking-[0.2em] transform skew-x-12">
                        REDEFINED
                    </h2>
                </div>
            </PageBanner>

            {/* Philosophy Intro */}
            <section className="py-20 max-w-7xl mx-auto px-4">
                <div className="flex flex-col md:flex-row items-center gap-12">
                    <motion.div
                        className="md:w-1/3"
                        {...fadeIn}
                    >
                        <img src="/assets/images/capital-beauty/capital-of-china.png" alt="Capital Calligraphy" className="w-full max-w-sm mx-auto" />
                    </motion.div>
                    <motion.div
                        className="md:w-2/3 text-center md:text-right"
                        {...fadeIn}
                        transition={{ delay: 0.2 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-light leading-relaxed text-gray-800">
                            As the <span className="font-bold">CAPITAL</span> of China, Beijing<br />
                            embodies the unity of tradition and<br />
                            modernity, conveying a temperament of<br />
                            order, firmness, and trustworthiness, and<br />
                            symbolizing the confidence, glory, and<br />
                            dreams of Chinese culture.
                        </h2>
                    </motion.div>
                </div>
            </section>

            {/* Banner Strip */}
            <div className="w-full h-24 bg-gray-600 flex items-center justify-around overflow-hidden relative">
                {/* This matches the 'GLO' 'DRE' 'AM' strip in mockup. 
                     I see files glory-dream-1.png etc. 
                     I will just place them as a collage strip or simple background for now.
                 */}
                <img src="/assets/images/capital-beauty/glory-dream-1.png" alt="Glory Dream" className="h-full object-cover opacity-80" />
                <div className="text-white text-4xl font-bold tracking-[0.5em] absolute inset-0 flex items-center justify-center bg-black/30">
                    GLORY DREAM
                </div>
            </div>

            {/* Keywords Mosaic */}
            <section className="py-24 max-w-7xl mx-auto px-4">
                <motion.div
                    className="text-center mb-16"
                    {...fadeIn}
                >
                    <h3 className="text-2xl font-bold mb-2 text-gray-800 uppercase tracking-widest">Capital Beauty:</h3>
                    <h2 className="text-4xl font-extrabold text-gray-900 uppercase">Keywords of the<br />Design Concept Are...</h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Confidence */}
                    <div className="relative group overflow-hidden h-96">
                        <img src="/assets/images/capital-beauty/confidence.png" alt="Confidence" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <h3 className="text-white text-3xl font-bold uppercase tracking-widest border-b-2 border-white pb-2">Confidence</h3>
                        </div>
                    </div>

                    {/* Oriental Style */}
                    <div className="relative group overflow-hidden h-96">
                        <img src="/assets/images/capital-beauty/oriental-style.png" alt="Oriental Style" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <h3 className="text-white text-3xl font-bold uppercase tracking-widest border-b-2 border-white pb-2">Oriental Style</h3>
                        </div>
                    </div>

                    {/* Warmth */}
                    <div className="relative group overflow-hidden h-[500px] md:-mt-24">
                        <img src="/assets/images/capital-beauty/warmth.png" alt="Warmth" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <h3 className="text-white text-3xl font-bold uppercase tracking-widest border-b-2 border-white pb-2">Warmth</h3>
                        </div>
                    </div>

                    {/* Color */}
                    <div className="relative group overflow-hidden h-[500px] md:-mt-24">
                        <img src="/assets/images/capital-beauty/color.jpg" alt="Color" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <h3 className="text-white text-3xl font-bold uppercase tracking-widest border-b-2 border-white pb-2">Color</h3>
                        </div>
                    </div>
                </div>
            </section>

            {/* Leaving Blank - Dark Section */}
            <section className="bg-black text-white py-24">
                <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center">
                    <div className="md:w-1/2">
                        <img src="/assets/images/capital-beauty/blue-flask.png" alt="Leaving Blank Essence" className="w-full max-w-md mx-auto" />
                    </div>
                    <div className="md:w-1/2 text-center md:text-left mt-12 md:mt-0">
                        <h3 className="text-4xl md:text-6xl font-thin tracking-widest mb-4">THE<br /><span className="font-normal">ESSENCE</span><br />OF</h3>
                        <h2 className="text-5xl md:text-7xl font-bold mb-8 uppercase text-gray-200">'Leaving<br />Blank'</h2>
                        <p className="text-2xl md:text-3xl text-gray-400 font-light uppercase tracking-widest leading-relaxed">
                            Expands<br />New<br />Reverie
                        </p>
                    </div>
                </div>
            </section>

            {/* Modern Simplicity */}
            <section className="py-24 max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 uppercase">Modern<br />Simplicity</h2>
                        <p className="text-gray-600 leading-relaxed text-justify">
                            To meet user needs for a minimalist and clear interaction experience,
                            the design team simplified unnecessary elements, ensuring a focus on function
                            while maintaining a sleek, modern aesthetic.
                        </p>
                    </div>
                    <div>
                        <img src="/assets/images/capital-beauty/modern-simplicity.png" alt="Modern Simplicity Interior" className="w-full rounded-lg shadow-2xl" />
                    </div>
                </div>
            </section>

            {/* Warm Experience */}
            <section className="py-24 max-w-7xl mx-auto px-4 bg-gray-50 mb-20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    <div className="order-2 md:order-1">
                        <img src="/assets/images/capital-beauty/warm-experience.png" alt="Warm Experience Team" className="w-full rounded-lg shadow-2xl grayscale hover:grayscale-0 transition-all duration-500" />
                    </div>
                    <div className="order-1 md:order-2 text-right">
                        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-8 uppercase">Warm<br />Experience</h2>
                        <p className="text-gray-600 leading-relaxed text-justify md:text-right pl-8">
                            Combining technology with care, we create a cabin environment
                            that feels like a second home. Soft materials, ambient lighting,
                            and intuitive controls work together to deliver a truly warm and inviting journey.
                        </p>
                    </div>
                </div>
            </section>

            {/* Bottom Banner */}
            <div className="w-full h-[60vh] overflow-hidden mt-12">
                <img
                    src="/assets/images/capital-beauty/437_1718608580capital-banner.png"
                    alt="Capital Beauty Footer Banner"
                    className="w-full h-full object-cover"
                />
            </div>

        </div>
    );
};

export default CapitalBeautyPage;
